import { toast } from 'sonner';
import React, { useState } from 'react';
import { Alert, AlertTitle } from '@mui/material';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { useAuth } from '../../../../../hooks/index.js';
import { generateUploadPromises, UploadFileModal, UploadFilesFormData, useFileUploader } from '../../../../file-upload';
import { getStepConfig } from '../../getStepConfig';
import { determineIfStepIsDisabled } from '../../utils';
import { usePatientIntakeStatusContext } from '../../hooks';
import { ActionPrompt, ViewDocumentModal } from '../../components';
import { toggleBaseStepVisibilityWithToast } from './utils';
import type { BaseStepsBaseProps } from './BaseSteps.types.ts';

const UploadFileModalDescription = () => (
  <Alert severity="info" icon={false}>
    <AlertTitle>Please upload your health summary</AlertTitle>
    This is a clinical record that can include your diagnosed conditions and the medicines you take.
  </Alert>
);

type UploadModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose }) => {
  const { user, userType } = useAuth();

  const { uploader } = useFileUploader();

  const handleFileUploadOnSubmit = async ({ files, description }: UploadFilesFormData) => {
    if (!user || !userType || !user.email) {
      throw new Error('User not found');
    }

    const generateFileUploadData: Parameters<
      typeof generateUploadPromises<'PATIENT_DOCUMENT_UPLOAD'>
    >[0]['generateFileUploadData'] = ({ file }) => ({
      file,
      type: 'PATIENT_DOCUMENT_UPLOAD',
      ownerId: user.uid,
      uploaderId: user.uid,
      fileName: file.name,
      metadata: {
        patientId: user.uid,
        description: description.trim(),
        type: 'HEALTH_SUMMARY',
        data: null,
      },
    });

    return await generateUploadPromises({ files, uploader, generateFileUploadData });
  };

  return (
    <UploadFileModal
      noToast
      maxFiles={1}
      isOpen={isOpen}
      handleCloseOnClick={onClose}
      onSubmit={handleFileUploadOnSubmit}
      description={<UploadFileModalDescription />}
      defaultValues={{
        description: 'My health summary',
      }}
    />
  );
};

type UploadDocumentActionModalProps = {
  step: IntakeStatusBaseSteps['UPLOAD_HEALTHCARE_SUMMARY'];
  isOpen: boolean;
  onClose: () => void;
};

const UploadDocumentActionModal: React.FC<UploadDocumentActionModalProps> = ({ step, isOpen, onClose }) => {
  const { status } = step.currentStatus;

  if (status === 'COMPLETED') {
    return <ViewDocumentModal step={step} type="PATIENT_DOCUMENT_UPLOAD" isOpen={isOpen} onClose={onClose} />;
  }

  return <UploadModal isOpen={isOpen} onClose={onClose} />;
};

interface HealthSummaryFormBaseStepActionProps extends BaseStepsBaseProps {}

export const HealthSummaryFormBaseStepAction: React.FC<HealthSummaryFormBaseStepActionProps> = ({ onEdit }) => {
  const { user, userType } = useAuth();
  const { patientId, baseSteps, isHealthcareProvider } = usePatientIntakeStatusContext();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const step = baseSteps?.UPLOAD_HEALTHCARE_SUMMARY;

  const handleOnClick = () => {
    setIsUploadModalOpen(true);
  };

  const handleOnEdit = () => {
    onEdit();
  };

  const handleOnToggleDisabled = async () => {
    if (!step) {
      toast.error('Step not found');

      return;
    }

    if (!user || !userType || !user.uid || !user.email) {
      toast.error('Please login to continue');

      return;
    }

    const isCurrentlyDisabled = determineIfStepIsDisabled({ step });

    await toggleBaseStepVisibilityWithToast({
      step,
      patientId,
      isCurrentlyDisabled,
      stepId: 'UPLOAD_HEALTHCARE_SUMMARY',
      context: 'upload healthcare summary',
      actionedBy: {
        id: user.uid,
        email: user.email,
        name: null,
        userType,
      },
    }).unwrap();
  };

  if (!step) {
    return null;
  }

  const stepConfig = getStepConfig('UPLOAD_HEALTHCARE_SUMMARY');

  const title = stepConfig.title({
    step,
    isHealthcareProvider,
  });

  const description = stepConfig.statuses[step.currentStatus.status].description({
    step,
    isHealthcareProvider,
  });

  return (
    <>
      <ActionPrompt
        step={step}
        title={title}
        description={description}
        onClick={handleOnClick}
        onEdit={handleOnEdit}
        onToggleDisabled={handleOnToggleDisabled}
      />

      <UploadDocumentActionModal step={step} isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} />
    </>
  );
};
